import React, { useState } from 'react'
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material'
import { NOTE_TYPES, NOTE_TYPE_LABELS } from '../../utils/guided'
import type { GuideNoteDraft, GuideNoteSaveResult } from '../../hooks/useGuidedResponse'
import type { GuideNoteView, GuideNoteType, GuidePoint } from '../../types/gotypes'

export interface GuideNoteDialogProps {
  /** The note being edited; null adds a new one. */
  note: GuideNoteView | null
  /** The point a new note starts under (null: none). */
  initialPointId?: string | null
  points: GuidePoint[]
  busy?: boolean
  /** Save against `base` (the version the person edited); null adds. */
  onSave: (base: GuideNoteView | null, draft: GuideNoteDraft) => Promise<GuideNoteSaveResult>
  /** Delete the note; absent when adding. */
  onDelete?: (note: GuideNoteView) => Promise<boolean>
  onClose: () => void
}

const NO_POINT = '_none'

/**
 * Add or correct one note. A save names the version the person started from;
 * when the agent or another tab changed the note meanwhile, the server says
 * so (409) and the dialog shows the latest beside what the person typed: they
 * keep theirs (Save mine, against the latest version) or take the latest.
 * The host mounts it per note (key), so the form starts from that note.
 */
const GuideNoteDialog: React.FC<GuideNoteDialogProps> = ({ note, initialPointId = null, points, busy, onSave, onDelete, onClose }) => {
  const [base, setBase] = useState<GuideNoteView | null>(note)
  const [title, setTitle] = useState(note?.title ?? '')
  const [body, setBody] = useState(note?.body ?? '')
  const [type, setType] = useState<GuideNoteType>(note?.type ?? 'fact')
  const [pointId, setPointId] = useState<string>((note ? note.point_id : initialPointId) ?? NO_POINT)
  const [conflict, setConflict] = useState<GuideNoteView | null>(null)
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const canSave = title.trim() !== '' && body.trim() !== '' && !busy

  const handleSave = async () => {
    setError('')
    const result = await onSave(base, { title: title.trim(), body: body.trim(), type, point_id: pointId === NO_POINT ? null : pointId })
    if (result.status === 'saved') {
      onClose()
      return
    }
    if (result.status === 'conflict') {
      // The next save is against the latest version, so it replaces it with what is typed here.
      setConflict(result.note)
      setBase(result.note)
      return
    }
    setError(result.error)
  }

  const handleUseLatest = () => {
    if (!conflict) return
    setTitle(conflict.title)
    setBody(conflict.body)
    setType(conflict.type)
    setPointId(conflict.point_id ?? NO_POINT)
    setConflict(null)
  }

  const handleDelete = async () => {
    if (!note || !onDelete) return
    if (await onDelete(note)) onClose()
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="guide-note-dialog-title">
      <DialogTitle id="guide-note-dialog-title">{note ? 'Edit note' : 'Add a note'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {conflict && (
          <Alert severity="warning" data-testid="guide-note-conflict">
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              This note changed while you were editing it. The latest version is below. Save mine replaces it with what you typed.
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{conflict.title}</Typography>
            <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{conflict.body}</Typography>
            <Button size="small" color="info" onClick={handleUseLatest} sx={{ mt: 1, textTransform: 'none' }}>
              Use the latest
            </Button>
          </Alert>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small" inputProps={{ maxLength: 200 }} />
        <TextField label="Note" value={body} onChange={(e) => setBody(e.target.value)} fullWidth multiline minRows={4} maxRows={14} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField select label="Kind" value={type} onChange={(e) => setType(e.target.value as GuideNoteType)} size="small" sx={{ flex: 1 }}>
            {NOTE_TYPES.map((t) => <MenuItem key={t} value={t}>{NOTE_TYPE_LABELS[t]}</MenuItem>)}
          </TextField>
          <TextField select label="Agenda point" value={pointId} onChange={(e) => setPointId(e.target.value)} size="small" sx={{ flex: 1 }}>
            <MenuItem value={NO_POINT}>No point</MenuItem>
            {points.map((p, i) => <MenuItem key={p.id} value={p.id}>{`${i + 1}. ${p.title}`}</MenuItem>)}
          </TextField>
        </Box>
        {confirmDelete && (
          <Alert
            severity="warning"
            action={(
              <Button color="inherit" size="small" onClick={handleDelete} disabled={busy}>
                Delete note
              </Button>
            )}
          >
            Delete this note? The person reading your answers will not see it.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {note && onDelete && !confirmDelete && (
          <Button color="error" onClick={() => setConfirmDelete(true)} sx={{ mr: 'auto' }}>
            Delete
          </Button>
        )}
        <Button color="info" onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="info" disableElevation onClick={handleSave} disabled={!canSave}>
          {conflict ? 'Save mine' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default GuideNoteDialog
